import * as d3 from 'd3';
import { SCALING_FUNCTIONS } from 'widgets/chartUtils';
import { WCWord, LayedOutWCWord, WCFunction, WCUpdateFunc, ConfigType} from './types'
import {getTextSizing} from './util'
var cloud = require('d3-cloud');
var {createCanvas} = require("canvas");

/**
 * A word cloud based on the original Wordle visualization.
 * @param svg
 * @returns {{clear: clear, update: externalUpdate}}
 * @constructor
 */
export const WordCloud = (svg, updateTooltip) => {
	let configOptions:ConfigType

	/**
	 * uses d3-cloud to compute and return the layout of the cloud
	 */
	let _makeLayout: WCFunction
	_makeLayout = function (words:readonly WCWord[], height, width, sizeRatio) {
		const myWords:WCWord[] = JSON.parse(JSON.stringify(words))
		const rotationScale = d3.scaleLinear()
							.domain([0,configOptions.numOrientations])
							.range([configOptions.minRotate,configOptions.maxRotate])
		const rotationFunction = () => rotationScale(
			~~(Math.random()*configOptions.numOrientations)
		)
		myWords.forEach((w)=>{
			w.r=rotationFunction()
			const scalingFunc = SCALING_FUNCTIONS.find(
				(i)=>i.value===configOptions.sizeScale
			).f
			w.relSize = scalingFunc(w.relSize)
		})

		const sizeScale=_findScale(myWords,height,width,sizeRatio)

		myWords.forEach((w)=>{
			w.relSize = sizeScale(w.relSize)
		})

		return (
			cloud().size([width, height])
				.canvas(function() { return createCanvas(1, 1); })
				.words(myWords)
				.rotate((w:LayedOutWCWord)=>w.r)
				.font(configOptions.font)
				.fontSize(function(w:LayedOutWCWord) { return w.relSize; })
				.timeInterval(100)
		)
	}

	/**
	 * This function calculates an 
	 * appropriate font scale for the words. It will return
	 * a d3 scale function
	 */
	let _findScale:WCFunction
	_findScale = function (words:readonly WCWord[], height, width, sizeRatio) {
		// Make new object to not edit original objects
		const myWords:WCWord[] = JSON.parse(JSON.stringify(words))

		//  get max value
		let maxSize = 0;
		myWords.forEach((w) => {
			maxSize = Math.max(maxSize,w.relSize)
		})

		// Standardize values
		const standardRangeMax = 100
		const standardScale = d3.scaleLinear()
									.domain([0,maxSize])
									.range([configOptions.minFontSize,standardRangeMax])
		myWords.forEach((w) => {
			w.relSize = standardScale(w.relSize)
		})
		
		// find total area and max width/height of any word
		let totalArea = 0
		let maxWidth = 0
		let maxHeight = 0
		for (let word of myWords) {
			const textSizing = getTextSizing(word.text,word.r,configOptions.font,word.relSize)
			totalArea += textSizing.area
			maxWidth = Math.max(maxWidth,textSizing.width)
			maxHeight = Math.max(maxHeight,textSizing.height)
		}

		// Find dimensions of correct proportions that equal total area needed
		const dimensionRatio = width/height
		const neededWidthByArea = Math.sqrt(dimensionRatio*totalArea)
		const neededWidth = Math.max(neededWidthByArea,maxWidth)
		const neededHeight = Math.max(totalArea/neededWidthByArea,maxHeight)

		// Create adjusted scale
		const heightRatio = height/neededHeight
		const widthRatio = width/neededWidth
		const sizeAdjustRatio = Math.max(heightRatio,widthRatio)
		return d3.scaleLinear()
					.domain([0,maxSize])
					.range([configOptions.minFontSize
							,standardRangeMax*sizeAdjustRatio*sizeRatio])
	}

	/**
	 * renders the words after d3-cloud has defined their layout
	 */
	function _draw(words:LayedOutWCWord[],layout) {
		var wordCloud = d3.select(svg)
			.attr("transform", "translate(" + layout.size()[0] / 2 + "," + layout.size()[1] / 2 + ")")
			.selectAll("g text")
			.data(words, function(d:LayedOutWCWord) { return d.text; })

		//Entering words
		wordCloud.enter()
			.append("text")
			.style("font-family", configOptions.font)
			.style("fill", (d:LayedOutWCWord)=>d.color)
			.attr("text-anchor", "middle")
			.attr('font-size', 1)
			.text(function(d) { return d.text; })
			.transition()
				.duration(600)
				.style("font-size", function(d:LayedOutWCWord) { return d.size + "px"; })
				.attr("transform", function(d:LayedOutWCWord) {
					return "translate(" + [d.x, d.y] + ")rotate(" + d.r + ")";
				})
				.style("fill-opacity", 1);

		//Entering and existing words
		wordCloud
			.transition()
				.duration(600)
				.style("font-size", function(d:LayedOutWCWord) { return d.size + "px"; })
				.attr("transform", function(d:LayedOutWCWord) {
					return "translate(" + [d.x, d.y] + ")rotate(" + d.r + ")";
				})
				.style("fill-opacity", 1)
				.style("font-family", configOptions.font)
				.style("fill", (d:LayedOutWCWord)=>d.color)

		//Exiting words
		wordCloud.exit()
			.transition()
				.duration(200)
				.style('fill-opacity', 1e-6)
				.attr('font-size', 1)
				.remove();
		return words.length
	}

	/**
	 * Will display the WCWords in words in the svg this function was
	 * initialized to in a box bounded by height and width.
	 */
	let update: WCUpdateFunc
	update = function (config,words, height, width) {
		if(config) configOptions=config;

		// Keep trying to display all the words, decreasing their size a little
		// every time until maxTries is reached.
		const expectedWords = words.length
		let renderedWords = 0;
		let numTries = 0;
		let sizeRatio = 1;
		const maxTries = 100;
		const decreaseOnRetry = .9;

		// Only draw if all the words were successfully placed
		const endFunc = (d,l) => {
			if (d.length === expectedWords) {
				_draw(d,l)
			}
			renderedWords = d.length
		}

		while (renderedWords !== expectedWords && numTries <= maxTries) {
			// Create layout and then use it to draw the cloud
			const layout = _makeLayout(words,height,width,sizeRatio)
			layout.on("end", d => endFunc(d,layout))
				  .start();
			numTries++;
			sizeRatio = sizeRatio*decreaseOnRetry;
		}
	}

	/**
	 * removes everything from the svg
	 */
	function clear() {
		d3.select(svg).selectAll('g').remove();
	}

	return {
		update: update,
		clear: clear,
	};
};